import { clsx } from 'clsx';
import React, { CSSProperties, PropsWithChildren, useCallback, useRef } from 'react';
import { HoverEvents } from 'react-aria';
import { Button as AriaButton, Link as AriaLink, PressEvent } from 'react-aria-components';
import CircleSpinner from '~/components/data-display/CircleSpinner/CircleSpinner';
import { isDefined } from '~/types/typeGuards';
import styles from './Button.module.scss';
import { HStack } from '../layout/Stack';

export type ButtonProps = {
  autoFocus?: boolean;
  action?: 'confirm' | 'destructive';
  className?: string;
  'data-testid'?: string;
  'data-intercom-target'?: string;
  disabled?: boolean;
  fullWidth?: boolean;
  Icon?: React.FunctionComponent<React.SVGProps<SVGSVGElement>>;
  loading?: boolean;
  onClick?: (e: PressEvent) => void;
  onPressStart?: (e: PressEvent) => void;
  onHoverStart?: HoverEvents['onHoverStart'];
  onHoverEnd?: HoverEvents['onHoverEnd'];
  onHoverChange?: HoverEvents['onHoverChange'];
  variant?: 'outlined' | 'ghost' | 'link';
  size?: 'small' | 'medium' | 'large';
  style?: CSSProperties;
  contentAlignment?: 'space-between' | 'center' | 'start' | 'end';
  contentGap?: 0.5 | 1 | 1.5 | 2 | 3 | 4 | 5;
  form?: string;
} & ({ href?: string; target?: '_blank'; rel?: 'noopener noreferrer' } | { type?: 'button' | 'submit' });

const Button = React.forwardRef<HTMLElement, PropsWithChildren<ButtonProps>>((props, ref) => {
  const {
    autoFocus,
    action,
    children,
    className,
    'data-testid': testId,
    'data-intercom-target': intercomTarget,
    disabled,
    fullWidth,
    Icon,
    loading,
    onClick,
    onPressStart,
    onHoverStart,
    onHoverEnd,
    onHoverChange,
    size = 'medium',
    style,
    variant,
    contentAlignment = 'space-between',
    contentGap,
    ...attrs
  } = props;
  const myRef = useRef<HTMLElement | null>(null);

  const computedClassName = clsx(
    styles.button,
    {
      [styles.fullWidth!]: fullWidth,
      [styles.loading!]: loading,
      [styles.destructive!]: action === 'destructive',
      [styles.confirm!]: action === 'confirm',
      [styles.contentAlignmentCenter!]: contentAlignment === 'center',
      [styles.contentAlignmentStart!]: contentAlignment === 'start',
      [styles.contentAlignmentEnd!]: contentAlignment === 'end',
    },
    styles[size],
    variant ? styles[variant] : null,
    className,
  );

  const setRef = useCallback(
    (element: HTMLElement | null) => {
      if (typeof ref === 'function') {
        ref(element);
      } else if (ref) {
        ref.current = element;
      }
      myRef.current = element;
    },
    [ref],
  );

  if ('href' in props) {
    return (
      <AriaLink
        {...attrs}
        autoFocus={autoFocus}
        data-auto-focus={autoFocus}
        className={computedClassName}
        href={props.href}
        target={props.target}
        isDisabled={disabled || loading}
        onPress={onClick}
        onPressStart={onPressStart}
        onHoverStart={onHoverStart}
        onHoverEnd={onHoverEnd}
        onHoverChange={onHoverChange}
        ref={setRef}
        style={style}
      >
        <CircleSpinner className={styles.spinner} />
        <HStack align="center" justify="center" gap={contentGap} className={styles.content}>
          {children}
          {Icon && <Icon className={styles.icon} />}
        </HStack>
      </AriaLink>
    );
  }

  const type = 'type' in props && isDefined(props.type) ? props.type : 'button';
  return (
    <AriaButton
      {...attrs}
      autoFocus={autoFocus}
      data-auto-focus={autoFocus}
      className={computedClassName}
      data-testid={testId}
      data-intercom-target={intercomTarget}
      isDisabled={disabled || loading}
      onPress={onClick}
      onPressStart={onPressStart}
      onHoverStart={onHoverStart}
      onHoverEnd={onHoverEnd}
      onHoverChange={onHoverChange}
      ref={setRef}
      style={style}
      type={type}
    >
      <CircleSpinner className={styles.spinner} />
      <HStack align="center" justify="center" gap={contentGap} className={styles.content}>
        {children}
        {Icon && <Icon className={styles.icon} />}
      </HStack>
    </AriaButton>
  );
});

Button.displayName = 'Button';

export default Button;
